'use client'

import BurgerButton from '@/components/ui/BurgerButton'
import { LanguageToggle } from '@/components/ui/LanguageToggle'
import { TimeDisplay } from '@/components/ui/TimeDisplay'
import { Link, usePathname } from '@/i18n/routing'
import { trackNavClick, trackNavOpen, type NavVariant } from '@/utils/tracking'
import { IconArrowRight, IconChevronDown } from '@tabler/icons-react'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { CSSProperties, KeyboardEvent, MouseEvent, PointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getActiveMenuId, isMenuWithColumns, mainMenu } from '../menu'
import MegaMenuPanel from './MegaMenuPanel'
import MobileNavLink from './MobileNavLink'
import MobileNavSection from './MobileNavSection'
import NavLink, { navItemClassName } from './NavLink'

// Hover-Absicht wie bei devin.ai: beim ersten Oeffnen kurz zoegern, damit ein
// Ueberfahren der Leiste auf dem Weg zum Seiteninhalt kein Panel aufreisst. Ist
// schon eins offen, wechselt der Inhalt sofort. Beim Verlassen bleibt etwas
// Luft, um schraeg von einem Punkt ins Panel zu fahren.
const OPEN_DELAY = 80
const CLOSE_DELAY = 180
// Muss zur duration-300 der Hoehenanimation passen: so lange bleibt der
// Panelinhalt beim Schliessen stehen, statt vor der Hoehe zu verschwinden
const PANEL_TRANSITION_MS = 300
// nav_open zaehlt erst, wenn ein Panel so lange offen bleibt. Ein Ueberfahren
// der Leiste oeffnet sonst im Vorbeigehen zwei, drei Panels und blaeht die
// Zahl auf, ohne dass jemand hineingesehen hat.
const OPEN_TRACK_DELAY = 400
// Ab dieser Scrolltiefe rueckt die Desktop-Leiste nach oben und wird flacher
const COMPACT_AFTER = 32
// Mobil: das offene Menue reicht bis auf diesen Abstand an den unteren Rand,
// derselbe wie oben (top-8)
const MOBILE_EDGE = 32

// Ein delegierter Handler je Navi statt onClick an jedem Link: die Ziele
// stehen als data-nav-link am Link, der Bereich als data-nav-menu darueber.
const trackNavClickFrom = (variant: NavVariant) => (event: MouseEvent<HTMLElement>) => {
  const link = event.target instanceof Element ? event.target.closest<HTMLElement>('[data-nav-link]') : null
  if (!link?.dataset.navLink) return
  const menu = link.closest<HTMLElement>('[data-nav-menu]')?.dataset.navMenu ?? 'bar'
  trackNavClick(menu, link.dataset.navLink, variant)
}

const panelId = (id: string) => `nav-panel-${id}`
const panelLinks = (id: string) => Array.from(document.querySelectorAll<HTMLAnchorElement>(`#${panelId(id)} a[href]`))

export const NavBar = () => {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  const t = useTranslations('navigation')
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const [highlightStyle, setHighlightStyle] = useState<CSSProperties>({ opacity: 0 })
  const listRef = useRef<HTMLUListElement>(null)
  const itemRefs = useRef<Record<string, HTMLLIElement | null>>({})
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const languageRef = useRef<HTMLDivElement>(null)

  const activeId = getActiveMenuId(pathname)

  // Mega-Menue: openMenuId ist das geoeffnete Panel, shownMenuId das gerade
  // sichtbare. Beim Schliessen bleibt shownMenuId fuer die Dauer der Animation
  // stehen und faellt dann auf null, damit kein zugeklapptes Panel im Layout bleibt.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [shownMenuId, setShownMenuId] = useState<string | null>(null)
  const [panelHeight, setPanelHeight] = useState(0)
  const panelInnerRef = useRef<HTMLDivElement>(null)
  const openedByHover = useRef(false)
  // Je Seite und Punkt nur ein nav_open: wer KI dreimal auf- und zuklappt, hat
  // sich das Panel einmal angesehen
  const trackedOpens = useRef(new Set<string>())
  const trackOpenOnce = useCallback((menu: string, variant: NavVariant) => {
    const key = `${variant}:${menu}`
    if (trackedOpens.current.has(key)) return
    trackedOpens.current.add(key)
    trackNavOpen(menu, variant)
  }, [])
  const openTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined)

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current)
    clearTimeout(closeTimer.current)
  }, [])

  const openMenu = useCallback(
    (id: string, viaHover: boolean) => {
      clearTimers()
      openedByHover.current = viaHover
      setOpenMenuId(id)
      setShownMenuId(id)
    },
    [clearTimers]
  )

  const closeMenu = useCallback(() => {
    clearTimers()
    setOpenMenuId(null)
  }, [clearTimers])

  const scheduleClose = useCallback(() => {
    clearTimers()
    closeTimer.current = setTimeout(closeMenu, CLOSE_DELAY)
  }, [clearTimers, closeMenu])

  useEffect(() => clearTimers, [clearTimers])

  useEffect(() => {
    if (openMenuId) return
    const timeout = setTimeout(() => setShownMenuId(null), PANEL_TRANSITION_MS)
    return () => clearTimeout(timeout)
  }, [openMenuId])

  useEffect(() => {
    if (!openMenuId) return
    const timeout = setTimeout(() => trackOpenOnce(openMenuId, 'desktop'), OPEN_TRACK_DELAY)
    return () => clearTimeout(timeout)
  }, [openMenuId, trackOpenOnce])

  // Hoehe des sichtbaren Panels messen. Beim Wechsel synchron vor dem Paint,
  // danach per ResizeObserver (Schriften laden nach, Fenster wird breiter)
  useLayoutEffect(() => {
    if (panelInnerRef.current) setPanelHeight(panelInnerRef.current.offsetHeight)
  }, [shownMenuId])

  useEffect(() => {
    const el = panelInnerRef.current
    if (!el) return
    const observer = new ResizeObserver(() => setPanelHeight(el.offsetHeight))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!openMenuId) return
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const trigger = triggerRefs.current[openMenuId]
      const focusWasInside = trigger?.closest('nav')?.contains(document.activeElement)
      closeMenu()
      if (focusWasInside) trigger?.focus()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [openMenuId, closeMenu])

  // Der gleitende Hintergrund folgt dem offenen Panel, sonst dem Hover, sonst
  // der aktuellen Seite. Ohne Treffer (Startseite, Rechtstexte) blendet er aus
  // und bleibt an Ort und Stelle, statt am zuletzt gehoverten Punkt zu kleben.
  const highlightId = openMenuId ?? hoveredId ?? activeId

  const updateHighlight = useCallback(() => {
    const el = highlightId ? itemRefs.current[highlightId] : null
    const parent = listRef.current
    if (!el || !parent) {
      setHighlightStyle((prev) => ({ ...prev, opacity: 0 }))
      return
    }
    const rect = el.getBoundingClientRect()
    const parentRect = parent.getBoundingClientRect()
    setHighlightStyle({
      width: `${rect.width}px`,
      left: `${rect.left - parentRect.left}px`,
      top: `${rect.top - parentRect.top}px`,
      height: `${rect.height}px`,
      borderRadius: '999px',
      opacity: 1
    })
  }, [highlightId])

  useLayoutEffect(() => {
    updateHighlight()
  }, [updateHighlight])

  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const observer = new ResizeObserver(updateHighlight)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateHighlight])

  useEffect(() => {
    setIsOpen(false)
    closeMenu()
    trackedOpens.current.clear()
  }, [pathname, closeMenu])

  const handleTriggerEnter = (id: string) => (event: PointerEvent) => {
    setHoveredId(id)
    // Touch und Stift oeffnen per Klick, sonst kaeme auf Tablets ein Tap als
    // Hover plus Klick an und schloesse das Panel sofort wieder
    if (event.pointerType !== 'mouse') return
    if (openMenuId) {
      openMenu(id, true)
      return
    }
    clearTimers()
    openTimer.current = setTimeout(() => openMenu(id, true), OPEN_DELAY)
  }

  const handleTriggerLeave = () => {
    setHoveredId(null)
    clearTimeout(openTimer.current)
  }

  const handleTriggerClick = (id: string) => {
    // Per Hover geoeffnet und dann geklickt: offen lassen statt zuzuklappen
    if (openMenuId === id && openedByHover.current) {
      openedByHover.current = false
      return
    }
    if (openMenuId === id) closeMenu()
    else openMenu(id, false)
  }

  // Die Panels stehen im DOM hinter der ganzen Leiste. Damit Tab vom Ausloeser
  // direkt ins offene Panel springt und von dessen Ende zum naechsten Punkt,
  // wird die Reihenfolge hier umgelenkt.
  const handleTriggerKeyDown = (id: string) => (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey && openMenuId === id)) {
      event.preventDefault()
      if (openMenuId !== id) openMenu(id, false)
      requestAnimationFrame(() => panelLinks(id)[0]?.focus())
    }
  }

  const focusAfter = (id: string) => {
    const next = itemRefs.current[id]?.nextElementSibling?.querySelector<HTMLElement>('a, button') ?? languageRef.current?.querySelector<HTMLElement>('button')
    next?.focus()
  }

  const handlePanelKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Tab' || !openMenuId) return
    const links = panelLinks(openMenuId)
    if (event.shiftKey && document.activeElement === links[0]) {
      event.preventDefault()
      triggerRefs.current[openMenuId]?.focus()
    } else if (!event.shiftKey && document.activeElement === links[links.length - 1]) {
      event.preventDefault()
      const id = openMenuId
      closeMenu()
      focusAfter(id)
    }
  }

  // Mobil: das Akkordeon der aktuellen Seite ist beim Oeffnen schon aufgeklappt
  const [mobileSectionId, setMobileSectionId] = useState<string | null>(null)
  const toggleMobileMenu = () => {
    if (!isOpen) {
      measureMobileBody()
      setMobileSectionId(mainMenu.find((item) => item.id === activeId && isMenuWithColumns(item))?.id ?? null)
      trackOpenOnce('burger', 'mobile')
    }
    setIsOpen(!isOpen)
  }
  const toggleMobileSection = (id: string) => {
    // Die beim Oeffnen schon aufgeklappte Gruppe zaehlt nicht, nur ein Tipp darauf
    if (mobileSectionId !== id) trackOpenOnce(id, 'mobile')
    setMobileSectionId((prev) => (prev === id ? null : id))
  }

  // Mobile Navi blendet sich beim Herunterscrollen aus und beim Hochscrollen
  // wieder ein. Fixiert belegt sie samt Abstand rund 133px, auf dem iPhone SE
  // ein Viertel des Schirms, und lag damit dauerhaft ueber Ueberschriften und
  // Formular. Die ersten 160px bleibt sie stehen, 8px Schwelle gegen Flackern
  // bei kleinen Ruckbewegungen. Ab lg greift die Klasse nicht.
  const [hideMobile, setHideMobile] = useState(false)
  const isOpenRef = useRef(isOpen)
  // Desktop: oben 64px Abstand plus 113px Leiste belegen bei 900px Hoehe ein
  // Fuenftel des Bildschirms. Beim Scrollen rueckt die Leiste deshalb auf 16px
  // nach oben und wird flacher (rund 73px). Die Seite verschiebt sich dabei
  // nicht, die Leiste ist fixiert.
  const [compact, setCompact] = useState(false)

  useEffect(() => {
    let lastY = window.scrollY
    let frame = 0

    const update = () => {
      frame = 0
      const y = window.scrollY
      const delta = y - lastY
      setCompact(y > COMPACT_AFTER)
      if (isOpenRef.current || y <= 160) {
        setHideMobile(false)
        lastY = y
        return
      }
      if (Math.abs(delta) < 8) return
      setHideMobile(delta > 0)
      lastY = y
    }

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update)
    }

    // Nach einem Reload mitten auf der Seite gleich im richtigen Zustand starten
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(frame)
    }
  }, [])

  useEffect(() => {
    isOpenRef.current = isOpen
    if (isOpen) setHideMobile(false)
  }, [isOpen])

  // Mobil fuellt das offene Menue den Bildschirm bis auf den Rand. Die Hoehe
  // wird in Pixeln gemessen, weil framer-motion nicht zwischen 0 und calc()
  // animieren kann. Rahmen = alles an der Karte ausser dem Menuekoerper.
  const mobileNavRef = useRef<HTMLElement>(null)
  const mobileBodyRef = useRef<HTMLDivElement>(null)
  const [mobileBodyHeight, setMobileBodyHeight] = useState(0)

  const measureMobileBody = useCallback(() => {
    const nav = mobileNavRef.current
    const body = mobileBodyRef.current
    if (!nav || !body) return
    const viewport = window.visualViewport?.height ?? window.innerHeight
    setMobileBodyHeight(Math.max(0, viewport - 2 * MOBILE_EDGE - (nav.offsetHeight - body.offsetHeight)))
  }, [])

  useEffect(() => {
    if (!isOpen) return
    // Seite dahinter sperren, sonst scrollt sie unter dem Menue mit
    const root = document.documentElement
    const previousOverflow = root.style.overflow
    root.style.overflow = 'hidden'

    const handleKeyDown = (event: globalThis.KeyboardEvent) => event.key === 'Escape' && setIsOpen(false)
    const viewport = window.visualViewport ?? window
    // Ab lg ist die mobile Navi ausgeblendet, die Scroll-Sperre darf dann nicht bleiben
    const desktop = window.matchMedia('(min-width: 64rem)')
    const handleDesktop = () => desktop.matches && setIsOpen(false)

    document.addEventListener('keydown', handleKeyDown)
    viewport.addEventListener('resize', measureMobileBody)
    desktop.addEventListener('change', handleDesktop)
    return () => {
      root.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      viewport.removeEventListener('resize', measureMobileBody)
      desktop.removeEventListener('change', handleDesktop)
    }
  }, [isOpen, measureMobileBody])

  return (
    <>
      {/* Weichzeichner ueber der Seite, solange ein Panel offen ist. Liegt
          ausserhalb der motion.nav: deren will-change und backdrop-filter
          machen sie zum Bezugsrahmen fuer fixed, inset-0 waere dann nur die Navi */}
      <div
        aria-hidden="true"
        onClick={closeMenu}
        className={classNames(
          'fixed inset-0 z-0 hidden lg:block bg-KMRS-Darkmode/40 backdrop-blur-[4px] transition-[opacity,visibility] duration-200 motion-reduce:transition-none',
          openMenuId ? 'visible opacity-100' : 'invisible opacity-0 pointer-events-none'
        )}
      />

      <motion.nav
        aria-label={t('mainNavigation')}
        variants={{
          hidden: { opacity: 0, y: -100 },
          enter: { opacity: 1, y: 0 }
        }}
        initial="hidden"
        exit="exit"
        animate="enter"
        transition={{ type: 'tween', ease: 'easeInOut', duration: 0.4 }}
        // Persistente Compositor-Ebene: ohne sie rastert der Browser den
        // backdrop-blur bei jedem Scroll-Frame neu (sichtbares Flackern),
        // sobald framer-motion nach der Einblendung transform:none setzt
        style={{ willChange: 'transform' }}
        onClick={trackNavClickFrom('desktop')}
        onPointerLeave={(event) => event.pointerType === 'mouse' && openMenuId && scheduleClose()}
        onBlur={(event) => {
          // Nur echtes Wegtabben schliesst. Ein Klick auf eine leere Stelle im
          // Panel nimmt den Fokus ohne neues Ziel (relatedTarget null) weg.
          if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) closeMenu()
        }}
        className={classNames(
          'max-w-[1440px] w-full mx-auto hidden lg:block z-100 rounded-3xl bg-clip-padding backdrop-filter backdrop-blur-md shadow-md border border-white/20 transition-[background-color,padding,translate] duration-300 motion-reduce:transition-none',
          // Offen liegt das Panel ueber Bildern und Text, dort reicht der
          // Glaseffekt allein nicht fuer gut lesbare Links. 95 statt weniger,
          // damit es auch ohne backdrop-filter lesbar bleibt
          openMenuId ? 'bg-KMRS-Darkmode/95' : 'bg-white/10',
          // -translate-y-12: von 64px Abstand (py-16 im Header) auf 16px
          compact ? 'px-6 py-3 -translate-y-12' : 'p-8'
        )}
      >
        <div className="flex flex-row items-center justify-between gap-x-6">
          <div className="flex flex-row items-center">
            <Link href="/" data-nav-menu="logo" data-nav-link="/">
              <Image
                src={'/images/logos/kommers-logo.svg'}
                width={50}
                height={50}
                className={classNames('mr-5 w-auto max-w-none object-cover transition-[height] duration-300 motion-reduce:transition-none', compact ? 'h-9' : 'h-[50px]')}
                alt="KOMMERS Logo"
              />
            </Link>
            {/* Unter 1440 px reicht die Breite nicht fuer Ort, Uhrzeit und alle Punkte */}
            <div className="hidden min-[1440px]:flex flex-row items-center">
              <div className="w-2 h-2 mr-2 rounded-full bg-accent" />
              <p className="text-KMRSx24-GreyLight whitespace-nowrap">
                Fürth, Deutschland <TimeDisplay />
              </p>
            </div>
          </div>
          <div className="flex flex-row items-center gap-x-4 xl:gap-x-6">
            <ul ref={listRef} data-nav-menu="bar" className="flex flex-row items-center gap-x-1 nav-list-liquid">
              <div className="nav-highlight-liquid" style={highlightStyle} aria-hidden="true" />
              {mainMenu.map((item) => {
                const isHighlighted = highlightId === item.id
                return (
                  <li
                    key={item.id}
                    ref={(el) => {
                      itemRefs.current[item.id] = el
                    }}
                    className="relative z-10 flex"
                    onPointerEnter={
                      isMenuWithColumns(item)
                        ? handleTriggerEnter(item.id)
                        : (event) => {
                            setHoveredId(item.id)
                            if (event.pointerType === 'mouse' && openMenuId) scheduleClose()
                          }
                    }
                    onPointerLeave={isMenuWithColumns(item) ? handleTriggerLeave : () => setHoveredId(null)}
                  >
                    {isMenuWithColumns(item) ? (
                      <button
                        ref={(el) => {
                          triggerRefs.current[item.id] = el
                        }}
                        type="button"
                        aria-expanded={openMenuId === item.id}
                        aria-controls={panelId(item.id)}
                        onClick={() => handleTriggerClick(item.id)}
                        onKeyDown={handleTriggerKeyDown(item.id)}
                        className={classNames(navItemClassName, 'relative z-10 flex items-center gap-x-1.5 cursor-pointer', isHighlighted ? 'text-selected-nav-tag' : 'text-white')}
                      >
                        {t(item.label)}
                        {/* -my-1: das Icon ist hoeher als die Zeilenhoehe und wuerde den Ausloeser sonst hoeher machen als die Links */}
                        <IconChevronDown
                          size={14}
                          stroke={2.5}
                          aria-hidden="true"
                          className={classNames('-my-1 transition-transform duration-200 motion-reduce:transition-none', openMenuId === item.id && 'rotate-180')}
                        />
                      </button>
                    ) : (
                      <NavLink href={item.href} title={t(item.label)} isActive={isHighlighted} />
                    )}
                  </li>
                )
              })}
            </ul>
            <div ref={languageRef}>
              <LanguageToggle nameFromXl />
            </div>
          </div>
        </div>

        <div
          className={classNames('overflow-hidden transition-[height,opacity] duration-300 ease-[cubic-bezier(0.77,0,0.18,1)] motion-reduce:transition-none', openMenuId ? 'opacity-100' : 'opacity-0')}
          style={{ height: openMenuId ? panelHeight : 0 }}
          inert={!openMenuId}
          onPointerEnter={() => clearTimeout(closeTimer.current)}
          onClick={(event) => {
            // Auch ein Link auf die aktuelle Seite soll das Panel schliessen,
            // dort aendert sich der Pfad nicht
            if (event.target instanceof Element && event.target.closest('a')) closeMenu()
          }}
          onKeyDown={handlePanelKeyDown}
        >
          <div ref={panelInnerRef}>
            {mainMenu.filter(isMenuWithColumns).map((item) => (
              <MegaMenuPanel key={item.id} id={panelId(item.id)} menuId={item.id} columns={item.columns} showContact={item.showContact} hidden={shownMenuId !== item.id} pathname={pathname} />
            ))}
          </div>
        </div>
      </motion.nav>

      <nav
        ref={mobileNavRef}
        aria-label={t('mainNavigation')}
        className={classNames(
          'mx-auto p-8 border border-accent rounded-3xl bg-KMRS-Darkmode fixed top-8 z-50 left-8 right-8 lg:hidden transition-[translate] duration-300 motion-reduce:transition-none',
          // Ganze Hoehe plus top-8 plus Schatten, sonst bleibt unten ein Streifen stehen
          hideMobile && 'translate-y-[calc(-100%-3rem)]'
        )}
        onClick={trackNavClickFrom('mobile')}
      >
        <div className="flex flex-row items-center justify-between">
          <div className="flex flex-row items-center">
            <Link href="/" data-nav-menu="logo" data-nav-link="/">
              <figure>
                <Image src={'/images/logos/kommers-logo.svg'} width={35} height={35} className="mr-5 aspect-square max-w-none object-cover" alt="KOMMERS Logo" />
              </figure>
            </Link>
            <div className="w-2 h-2 mr-2 rounded-full bg-accent" />
            <p className="text-KMRSx24-GreyLight font-light text-xs leading-3 line-clamp-6">
              Fürth, DE <TimeDisplay />
            </p>
          </div>
          <BurgerButton isOpen={isOpen} onClick={toggleMobileMenu} />
        </div>
        <motion.div ref={mobileBodyRef} className="h-0 overflow-hidden" initial={false} animate={{ height: isOpen ? mobileBodyHeight : 0 }}>
          <div className="flex h-full flex-col">
            {/* Die Liste scrollt, Beratung, Kontakt und Sprache bleiben unten stehen */}
            <div className="mt-8 min-h-0 flex-1 overflow-y-auto overscroll-contain border-t border-accent">
              <ul data-nav-menu="bar" className="flex flex-col gap-y-8 py-8">
                {mainMenu.map((item) =>
                  isMenuWithColumns(item) ? (
                    <MobileNavSection
                      key={item.id}
                      id={`mobile-${panelId(item.id)}`}
                      menuId={item.id}
                      label={t(item.label)}
                      columns={item.columns}
                      isOpen={mobileSectionId === item.id}
                      isActive={activeId === item.id}
                      pathname={pathname}
                      onToggle={() => toggleMobileSection(item.id)}
                    />
                  ) : (
                    <MobileNavLink key={item.id} href={item.href} title={t(item.label)} />
                  )
                )}
              </ul>
            </div>
            <div data-nav-menu="contact" className="flex shrink-0 flex-col gap-y-6 border-t border-accent pt-6">
              <Link
                href="/kostenlose-beratung"
                data-nav-link="/kostenlose-beratung"
                className="flex items-center justify-center gap-x-2 rounded-3xl bg-accent px-6 py-4 text-center font-medium text-white transition-colors hover:bg-accent/90"
              >
                {t('menu.freeConsultation')}
                <IconArrowRight size={18} aria-hidden="true" />
              </Link>
              <div className="flex items-end justify-between gap-x-4">
                <div className="flex flex-col text-sm text-accent">
                  <a href="tel:+4991147734580" data-nav-link="tel">
                    +49 (911) 477 345 80
                  </a>
                  <a href="mailto:hello@kommers.io" data-nav-link="mailto">
                    hello@kommers.io
                  </a>
                </div>
                {/* Unten am Rand: Auswahl nach oben oeffnen, sonst schneidet die Karte sie ab */}
                <LanguageToggle openUpwards />
              </div>
            </div>
          </div>
        </motion.div>
      </nav>
    </>
  )
}

export default NavBar
